import {
  type ChangeEvent,
  type ChangeEventHandler,
  type FocusEvent,
  type FocusEventHandler,
  useCallback,
} from "react";
import {
  type FieldValues,
  useController,
  type UseControllerProps,
  useFormContext,
} from "react-hook-form";
import {
  type TextFieldType,
  type ValidationOptions,
} from "@/components/common/formElements/types";
import { applyTextValidationOptions } from "@/components/common/formElements/utils/applyTextValidationOptions";
import { transformValidationOptions } from "@/components/common/formElements/utils/transformValidationOptions";
import { useTranslation } from "@/i18n/client";

export type TextFieldValidationOptions<TFieldValues extends FieldValues> =
  ValidationOptions<TFieldValues> & {
    postcode?: boolean;
    inFrontAllowedChars?: boolean;
  };

export interface UseTextFieldControllerProps<TFieldValues extends FieldValues>
  extends Omit<UseControllerProps<TFieldValues>, "rules"> {
  rules?: TextFieldValidationOptions<TFieldValues>;
  type?: TextFieldType;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onChange?: ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
}

export function useTextFieldController<TFieldValues extends FieldValues>(
  props: UseTextFieldControllerProps<TFieldValues>,
) {
  const {
    type = "text",
    name,
    onBlur,
    onChange,
    rules,
    ...textFieldControllerProps
  } = props;

  const { t } = useTranslation("form-elements");
  const { control, getValues } = useFormContext<TFieldValues>();

  const validationOptions = transformValidationOptions<TFieldValues>(
    t,
    rules,
    getValues,
  );
  const textFieldRules = applyTextValidationOptions<TFieldValues>(
    t,
    type,
    validationOptions,
  );
  const required = typeof textFieldRules.required !== "undefined";

  const controllerProps = useController<TFieldValues>({
    name,
    control,
    rules: textFieldRules,
    ...textFieldControllerProps,
  });
  const { field } = controllerProps;

  const trimValue = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      if (type === "number") return;

      const value = event.target.value;
      const trimmedValue = value.trim();

      if (value !== trimmedValue) {
        field.onChange(trimmedValue);
      }
    },
    [field, type],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value, valueAsNumber } = event.target;

      if (type === "number") {
        const newValue = valueAsNumber || value;
        field.onChange(newValue);
      } else {
        field.onChange(event);
      }

      if (onChange) {
        onChange(event);
      }
    },
    [field, onChange, type],
  );

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      trimValue(event);
      field.onBlur();

      if (onBlur) {
        onBlur(event);
      }
    },
    [field, onBlur, trimValue],
  );

  return {
    required,
    onChangeField: handleChange,
    onBlurField: handleBlur,
    ...controllerProps,
  };
}
